// hubs.ts
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { getEnvConfig } from "~/services/appConfig";
import { authService } from "~/services/authService";
import { AuthError } from "~/types/auth";

export class HubManager {
  private hubs: { [key: string]: HubConnection } = {};
  private connectionPromises: { [key: string]: Promise<HubConnection | null> } =
    {};
  private connectionId: string | null = null;

  /**
   * Per-hub connection IDs. This is critical for features like PDF progress
   * that subscribe to a specific hub ("self") for messages but must tell the
   * server the exact ConnectionId of *that* hub's connection (other hubs like
   * "workflowInstance" have their own separate connections/IDs).
   */
  private connectionIds: { [key: string]: string | null } = {};

  /**
   * Tracks active event subscriptions per "hubName:event" so we can provide
   * diagnostics and (in the future) smarter refcounted connection lifecycle.
   */
  private eventSubscriptions = new Map<string, Set<(...args: any[]) => void>>();

  /** The connection ID for the current hub connection */
  get ConnectionId() {
    return this.connectionId;
  }

  /**
   * Returns the ConnectionId associated with a specific hubName (e.g. "self").
   * Falls back to the last-known global ConnectionId if not known for that hub.
   * This allows PDF progress (and similar) to always target the exact connection
   * that has the PdfMsg listener, even if other hubs have been joined since.
   *
   * NOTE: This is a synchronous snapshot. The "ConnectionId" message from the
   * server can arrive shortly *after* getHub() resolves (after the WS is
   * established). For PDF progress (and any case where you need the ID to
   * send back to the server so it can target *this* connection for messages),
   * prefer the async `getConnectionId(hubName, url)` which waits for the ID.
   */
  public getHubConnectionId(hubName: string): string | null {
    return this.connectionIds[hubName] ?? this.connectionId;
  }

  /**
   * Ensures the named hub is connected and returns a ConnectionId that is
   * guaranteed to belong to *that* hub's connection (waiting briefly for the
   * server's "ConnectionId" message if necessary).
   *
   * This is the reliable way to obtain the ID you must pass to server APIs
   * (e.g. PDF generation) when you have (or will have) a listener on this
   * specific hub for progress / callback messages.
   */
  public async getConnectionId(
    hubName: string,
    url: string,
  ): Promise<string | null> {
    await this.getHub(hubName, url);

    if (this.connectionIds[hubName]) {
      return this.connectionIds[hubName];
    }

    // The "ConnectionId" message is sent by the server shortly after the
    // connection reaches Connected state. Wait for it (with a reasonable cap).
    return new Promise((resolve) => {
      const start = Date.now();
      const maxWaitMs = 4000;

      const tick = () => {
        if (this.connectionIds[hubName]) {
          resolve(this.connectionIds[hubName]);
          return;
        }
        if (Date.now() - start > maxWaitMs) {
          // Fall back to whatever we have (global or the passed-in one at call sites)
          resolve(this.connectionId);
          return;
        }
        setTimeout(tick, 40);
      };

      tick();
    });
  }

  public async getHub(
    hubName: string,
    url: string,
  ): Promise<HubConnection | null> {
    // If there's already a connection attempt in progress, wait for it
    if (Object.hasOwn(this.connectionPromises, hubName)) {
      return await this.connectionPromises[hubName];
    }

    // Create a new connection promise
    this.connectionPromises[hubName] = this._createConnection(hubName, url);

    try {
      const connection = await this.connectionPromises[hubName];

      // The server sends a "ConnectionId" message shortly after the hub
      // connection is established. For scenarios like PDF progress (where
      // we later need to tell the server "send PdfMsg back to this exact
      // client connection"), we must wait for that message so that
      // getHubConnectionId(hubName) and the per-hub map are populated
      // before the caller proceeds to use the ID.
      //
      // Only wait when we actually got a connection. If _createConnection
      // returned null (e.g. no auth token, or creation failed), do not
      // delay the caller with a pointless timeout.
      if (connection && !this.connectionIds[hubName]) {
        await this._waitForConnectionId(hubName, 3500);
      }

      return connection;
    } finally {
      // Clean up the promise after completion
      delete this.connectionPromises[hubName];
    }
  }

  private async _createConnection(
    hubName: string,
    url: string,
  ): Promise<HubConnection | null> {
    try {
      let token: string | null;
      try {
        token = await authService.getAccessTokenAsync();
      } catch (error) {
        if (error instanceof AuthError) {
          console.warn(
            `Auth not available for hub ${hubName}, skipping connection`,
          );
          return null;
        }
        throw error;
      }

      if (!token) {
        console.warn(
          `No token available for hub ${hubName}, skipping connection`,
        );
        return null;
      }

      // If connection already exists and is connected, return it.
      // Still ensure we have its ConnectionId (the message might have arrived
      // on a previous getHub, but in some reuse scenarios after restarts etc.
      // we double-check).
      if (
        this.hubs[hubName] &&
        this.hubs[hubName].state === HubConnectionState.Connected
      ) {
        if (!this.connectionIds[hubName]) {
          await this._waitForConnectionId(hubName, 2000);
        }
        return this.hubs[hubName];
      }

      // If connection exists but is in a bad state, clean it up
      if (this.hubs[hubName]) {
        try {
          await this.hubs[hubName].stop();
        } catch (error) {
          console.warn(`Error stopping existing hub ${hubName}:`, error);
        }
        delete this.hubs[hubName];
        delete this.connectionIds[hubName];
      }

      // Create new connection
      this.hubs[hubName] = new HubConnectionBuilder()
        .withUrl(getEnvConfig().serverUrl + "/hubs" + url, {
          accessTokenFactory: async () => {
            try {
              // console.log(
              //   `Getting access token for SignalR connection to ${hubName}...`,
              // );
              // Always get a fresh token for SignalR connections
              const freshToken = await authService.getAccessTokenAsync();
              if (!freshToken) {
                console.warn(
                  `No token available for SignalR connection to ${hubName}`,
                );
                return "";
              }
              // console.log(
              //   `Successfully obtained token for SignalR connection to ${hubName} (length: ${freshToken.length})`,
              // );
              return freshToken;
            } catch (error) {
              console.error(
                `Error getting token for SignalR connection to ${hubName}:`,
                error,
              );
              return "";
            }
          },
        })
        .configureLogging(
          getEnvConfig().debugHubs === "true"
            ? LogLevel.Debug
            : LogLevel.Warning,
        )
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff with jitter
            const delay = Math.min(
              1000 * Math.pow(2, retryContext.previousRetryCount),
              30000,
            );
            const jitter = Math.random() * 1000;
            return delay + jitter;
          },
        })
        .build();

      // Add error handlers
      this.hubs[hubName].onclose((error) => {
        if (error) {
          console.error(
            `SignalR connection ${hubName} closed with error:`,
            error,
          );
        } else {
          // console.log(`SignalR connection ${hubName} closed`);
        }
      });

      this.hubs[hubName].onreconnecting((error) => {
        console.warn(`SignalR connection ${hubName} reconnecting:`, error);
      });

      this.hubs[hubName].on("ConnectionId", (connectionId) => {
        // console.log(`SignalR connection ${hubName} has ID: ${connectionId}`);
        this.connectionIds[hubName] = connectionId;
        this.connectionId = connectionId;
      });

      await this.start(hubName);

      // Double-check that connection is actually established
      if (this.hubs[hubName].state !== HubConnectionState.Connected) {
        throw new Error(
          `Failed to establish connection for ${hubName}. Final state: ${this.hubs[hubName].state}`,
        );
      }

      return this.hubs[hubName];
    } catch (error) {
      console.error(`Failed to create hub ${hubName}:`, error);
      // Clean up failed connection
      if (this.hubs[hubName]) {
        try {
          await this.hubs[hubName].stop();
        } catch (stopError) {
          console.warn(`Error stopping failed hub ${hubName}:`, stopError);
        }
        delete this.hubs[hubName];
        delete this.connectionIds[hubName];
      }
      return null;
    }
  }

  public async start(hubName: string): Promise<void> {
    const connection = this.hubs[hubName];
    if (!connection) {
      throw new Error(`Hub ${hubName} does not exist`);
    }

    // console.log(
    //   `Starting SignalR connection for ${hubName}, current state: ${connection.state}`,
    // );
    const timeout = 15000; // Reduced to 15 seconds
    const pollInterval = 50; // Check every 50ms
    const disconnectTimeout = 5000;

    // Handle different connection states
    switch (connection.state) {
      case HubConnectionState.Disconnected:
        // console.log(`Starting SignalR connection for ${hubName}...`);
        try {
          // Add timeout to connection start
          const startTimeout = 20000; // 20 seconds
          const startPromise = connection.start();
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(
              () =>
                reject(
                  new Error(
                    `Connection start timed out after ${startTimeout}ms`,
                  ),
                ),
              startTimeout,
            );
          });

          await Promise.race([startPromise, timeoutPromise]);
          // console.log(`SignalR connection for ${hubName} started successfully`);
        } catch (error) {
          console.error(
            `Failed to start SignalR connection for ${hubName}:`,
            error,
          );
          throw error;
        }
        break;

      case HubConnectionState.Connecting: {
        // If already connecting, wait for it to complete with a more aggressive timeout
        // console.log(
        //   `SignalR connection for ${hubName} is already connecting, waiting...`,
        // );

        const startTime = Date.now();

        while (
          connection.state === HubConnectionState.Connecting &&
          Date.now() - startTime < timeout
        ) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        if (connection.state === HubConnectionState.Connecting) {
          console.error(
            `SignalR connection for ${hubName} timed out while connecting`,
          );
          // Force stop the stuck connection
          try {
            await connection.stop();
          } catch (stopError) {
            console.warn(
              `Error stopping stuck connection for ${hubName}:`,
              stopError,
            );
          }
          throw new Error(
            `SignalR connection for ${hubName} timed out while connecting`,
          );
        }

        if (connection.state !== HubConnectionState.Connected) {
          throw new Error(
            `SignalR connection for ${hubName} failed to connect. Final state: ${connection.state}`,
          );
        }

        // console.log(`SignalR connection for ${hubName} completed successfully`);
        break;
      }
      case HubConnectionState.Connected:
        // console.log(`SignalR connection for ${hubName} is already connected`);
        break;

      case HubConnectionState.Disconnecting: {
        // Wait for disconnection to complete, then try to reconnect
        // console.log(
        //   `SignalR connection for ${hubName} is disconnecting, waiting...`,
        // );

        const disconnectStartTime = Date.now();

        while (
          connection.state === HubConnectionState.Disconnecting &&
          Date.now() - disconnectStartTime < disconnectTimeout
        ) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        if (connection.state === HubConnectionState.Disconnecting) {
          throw new Error(
            `SignalR connection for ${hubName} timed out while disconnecting`,
          );
        }

        // Now try to start the connection
        if (connection.state === HubConnectionState.Disconnected) {
          await this.start(hubName); // Recursive call
        } else {
          throw new Error(
            `SignalR connection for ${hubName} is in unexpected state after disconnecting: ${connection.state}`,
          );
        }
        break;
      }
      default:
        throw new Error(
          `SignalR connection for ${hubName} is in unexpected state: ${connection.state}`,
        );
    }

    // Final verification
    if (connection.state !== HubConnectionState.Connected) {
      throw new Error(
        `SignalR connection for ${hubName} is not connected after start attempt. State: ${connection.state}`,
      );
    }
  }

  public stop(hubName: string): void {
    if (this.hubs[hubName]) {
      // console.log(`Stopping SignalR connection for ${hubName}...`);
      this.hubs[hubName].stop();
      delete this.hubs[hubName];
      delete this.connectionIds[hubName];
    }
  }

  public async restart(hubName: string): Promise<void> {
    // console.log(`Restarting SignalR connection for ${hubName}...`);
    this.stop(hubName);
    await this.start(hubName);
  }

  public async forceRestart(
    hubName: string,
    url: string,
  ): Promise<HubConnection | null> {
    // console.log(`Force restarting SignalR connection for ${hubName}...`);

    // Clean up existing connection completely
    if (this.hubs[hubName]) {
      try {
        await this.hubs[hubName].stop();
      } catch (error) {
        console.warn(
          `Error stopping connection during force restart for ${hubName}:`,
          error,
        );
      }
      delete this.hubs[hubName];
      delete this.connectionIds[hubName];
    }

    // Clear any pending connection promises
    delete this.connectionPromises[hubName];

    // Create a fresh connection
    return this.getHub(hubName, url);
  }

  public getConnectionState(hubName: string): HubConnectionState | null {
    return this.hubs[hubName]?.state || null;
  }

  public isConnected(hubName: string): boolean {
    return this.hubs[hubName]?.state === HubConnectionState.Connected;
  }

  public getAllConnectionStates(): Record<string, HubConnectionState | null> {
    const states: Record<string, HubConnectionState | null> = {};
    for (const [hubName, connection] of Object.entries(this.hubs)) {
      states[hubName] = connection?.state || null;
    }
    return states;
  }

  private async _waitForConnectionId(
    hubName: string,
    timeoutMs: number,
  ): Promise<void> {
    if (this.connectionIds[hubName]) return;

    return new Promise((resolve) => {
      const start = Date.now();

      const check = () => {
        if (this.connectionIds[hubName]) {
          resolve();
          return;
        }
        if (Date.now() - start > timeoutMs) {
          // Timed out; the caller will fall back to global ConnectionId or
          // whatever was passed in. This is the same fallback behavior as before.
          resolve();
          return;
        }
        setTimeout(check, 30);
      };

      check();
    });
  }

  /**
   * High-level safe subscription for shared hubs.
   *
   * - Ensures (or reuses) the named connection via getHub.
   * - Attaches the handler with .on.
   * - Returns a function that does .off(event, handler) — only your listener is removed.
   * - Tracks subscriptions for diagnostics / future refcounting.
   *
   * Usage:
   *   const unsub = await hubManager.subscribe("self", "/self", "PdfMsg", myHandler);
   *   // later
   *   unsub();
   */
  public async subscribe(
    hubName: string,
    url: string,
    eventName: string,
    handler: (...args: any[]) => void,
  ): Promise<() => void> {
    const connection = await this.getHub(hubName, url);
    if (!connection) {
      console.warn(
        `[hubManager] subscribe: no connection for ${hubName} (event: ${eventName})`,
      );
      return () => {};
    }

    connection.on(eventName, handler);

    const key = `${hubName}:${eventName}`;
    if (!this.eventSubscriptions.has(key)) {
      this.eventSubscriptions.set(key, new Set());
    }
    this.eventSubscriptions.get(key)!.add(handler);

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;

      const set = this.eventSubscriptions.get(key);
      if (set) {
        set.delete(handler);
        if (set.size === 0) {
          this.eventSubscriptions.delete(key);
        }
      }
      try {
        connection.off(eventName, handler);
      } catch {
        /* ignore */
      }
    };
  }

  /** Number of tracked handlers for a given hub/event (useful for debugging). */
  public getSubscriptionCount(hubName: string, eventName?: string): number {
    if (eventName) {
      return this.eventSubscriptions.get(`${hubName}:${eventName}`)?.size ?? 0;
    }
    let total = 0;
    for (const [k, set] of this.eventSubscriptions.entries()) {
      if (k.startsWith(`${hubName}:`)) total += set.size;
    }
    return total;
  }
}

export const hubManager = new HubManager();
